/* eslint-disable default-case */
import React, {
  Fragment,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import useRedirectIfVerified from "../../helpers/use-redirect-if-verified";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import AsyncSelect from "react-select/async";
import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import Select from "react-select";
import ReactModal from "react-modal";
import Checkbox from "@mui/material/Checkbox";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import moment from "moment-timezone";
import CreatableSelect from "react-select/creatable";
import isEqual from "lodash.isequal";

import FBOAutocomplete from "./fbo-autocomplete";
import CreateFBOModal from "./create-fbo-modal";

import { LoadingButton } from "../shared/loading-button";
import TextInput from "../text-input";
import TextAreaInput from "../textarea-input";
import ProspectsAutocomplete from "../prospects/prospects-autocomplete";
import CreateProspectModal from "../prospects/create-prospect-modal";
import OperatorAutocomplete from "../opi/operator-autocomplete";

import ai from "../../helpers/axios";
import timeFormatter from "../../helpers/timeFormatter";
import { formatTripDate } from "../../helpers/dates";
import PageHeader from "../shared/page-header";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";
import genders from "../../helpers/genders";
import DatePicker from "react-date-picker";

momentTimezone.moment = moment;

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const emailComponents = {
  DropdownIndicator: null,
};

const NEW_PASSENGER_TYPE = {
  value: "new",
  label: "New",
};

const EMPTY_PASSENGER_TYPE = {
  value: null,
  label: "N/A",
};

const getProspectPassengerType = (clientData) =>
  clientData
    ? {
      value: "prospect",
      label: [
        clientData.firstName,
        clientData.middleName,
        clientData.lastName,
        [
          clientData.dateOfBirth
            ? moment(clientData.dateOfBirth).format("M/D/YYYY")
            : "",
          clientData.weight ? `${clientData.weight}lb` : "",
          clientData.gender ? clientData.gender : "",
        ]
          .filter(Boolean)
          .join(", "),
        "(Prospect)",
      ].join(" "),
      firstName: clientData.firstName,
      middleName: clientData.middleName,
      lastName: clientData.lastName,
      dateOfBirth: clientData.dateOfBirth ? new Date(clientData.dateOfBirth) : null,
      weight: clientData.weight,
      gender: clientData.gender,
    }
    : NEW_PASSENGER_TYPE;

const getSavedPassengerType = (savedPassenger) => ({
  value: savedPassenger._id,
  label: [
    [
      savedPassenger.firstName,
      savedPassenger.middleName,
      savedPassenger.lastName,
    ]
      .filter(Boolean)
      .join(" "),
    savedPassenger.dateOfBirth
      ? moment(savedPassenger.dateOfBirth).format("M/D/YYYY")
      : "",
    savedPassenger.weight ? `${savedPassenger.weight}lb` : "",
    savedPassenger.gender ? savedPassenger.gender : "",
  ]
    .filter(Boolean)
    .join(", "),
  firstName: savedPassenger.firstName,
  middleName: savedPassenger.middleName,
  lastName: savedPassenger.lastName,
  dateOfBirth: savedPassenger.dateOfBirth ? new Date(savedPassenger.dateOfBirth) : null,
  weight: savedPassenger.weight,
  gender: savedPassenger.gender,
});

const formatOFROptionLabel = (ofr) =>
  `${ofr.operator?.name || "N/A"} ${ofr.aircraft?.name || "N/A"} ${
    ofr.tailNumber || ""
  } - ${ofr.totalClientPayment.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  })}`;

const copyObjectsArray = array => structuredClone(array);

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "fieldChanged": {
      if (action.field === "itineraryData" && action.value.emailOptions) {
        action.value.emailOptions.email = (
          action.value.emailOptions.email || []
        ).map((item) => ({
          label: item,
          value: item,
        }));
        action.value.emailOptions.cc = (action.value.emailOptions.cc || []).map(
          (item) => ({
            label: item,
            value: item,
          }),
        );
        action.value.emailOptions.bcc = (
          action.value.emailOptions.bcc || []
        ).map((item) => ({
          label: item,
          value: item,
        }));
      }

      return {
        ...state,
        isOFRRequired:
          action.field === "itineraryData" &&
          action.value?._id &&
          !action.value?.legs?.[0]?.ofrId
            ? false
            : state.isOFRRequired,
        [action.field]: action.value,
        ...(action.setToInitial && {
          initialFormValues: {
            ...state.initialFormValues,
            [action.field]: action.value,
          },
        }),
      };
    }
    case "emailOptionsFieldChanged": {
      return {
        ...state,
        itineraryData: {
          ...state.itineraryData,
          emailOptions: {
            ...(state.itineraryData.emailOptions || {}),
            [action.field]: action.value,
          },
        },
        itineraryDataChanged: true,
      };
    }
    case "tripContactDetailsChanged": {
      return {
        ...state,
        tripContactDetails: {
          ...state.tripContactDetails,
          [action.payload.field]: action.payload.value,
        },
      };
    }

    case "routingProcessed": {
      return {
        ...state,
        legs: action.value,
        routingProcessed: true,
      };
    }

    case "prospectAutoSelected": {
      let prospectOptions = [action.value, ...state.prospectOptions];

      return {
        ...state,
        selectedClient: action.value,
        prospectOptions,
      };
    }

    case "legAdded": {
      return {
        ...state,
        legs: [
          ...state.legs,
          {
            departureDate: "",
            departureAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            arrivalAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            passengers: "",
            aircraft: {
              name: "",
              seats: 8,
              yom: "",
              opi: "",
              id: "",
            },
            pax: [],
            tailNumber: "",
            crew: [],
            catering: [],
            groundTransportation: [],
            notes: [],
          },
        ],
        legsDataChanged: true,
      };
    }
    case "legDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray.splice(action.index, 1);
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }
    case "legFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex][action.payload.field] =
        action.payload.value;

      if (action.payload.field === "ofr") {
        newLegsArray[action.payload.legIndex].ofrId =
          action.payload.value?.value;
        newLegsArray[action.payload.legIndex].tailNumber =
          action.payload.value?.tailNumber;
        newLegsArray[action.payload.legIndex].aircraft = {
          id: action.payload.value?.aircraft?.id,
          name: action.payload.value?.aircraft?.name,
        };
        newLegsArray[action.payload.legIndex].selectedOperator = {
          id: action.payload.value?.operator?.id,
          name: action.payload.value?.operator?.name,
          label: action.payload.value?.operator?.name,
          value: action.payload.value?.operator?.id,
        };
      }

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }
    case "legOFRCopy": {
      const ofr = state.legs?.[0]?.ofr;
      return {
        ...state,
        legs: copyObjectsArray(state.legs).map((leg) => ({
          ...leg,
          ofr,
          ofrId: ofr?.value,
          tailNumber: ofr?.tailNumber,
          aircraft: {
            id: ofr?.aircraft?.id,
            name: ofr?.aircraft?.name,
          },
          selectedOperator: {
            id: ofr?.operator?.id,
            name: ofr?.operator?.name,
            label: ofr?.operator?.name,
            value: ofr?.operator?.id,
          },
        })),
      };
    }

    case "legPaxFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex].pax[action.payload.paxIndex][
        action.payload.field
        ] = action.payload.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "paxAdded": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].pax.push({
        firstName: "",
        middleName: "",
        lastName: "",
        dateOfBirth: null,
        weight: null,
        gender: null,
        type: NEW_PASSENGER_TYPE,
        save: false,
      });

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "paxDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].pax.splice(action.paxIndex, 1);

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legCrewFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex].crew[action.payload.memberIndex][
        action.payload.field
        ] = action.payload.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "crewAdded": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].crew.push({
        firstName: "",
        middleName: "",
        lastName: "",
      });

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "crewDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].crew.splice(action.memberIndex, 1);

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legGroundTransportationFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex].groundTransportation[
        action.payload.itemIndex
        ][action.payload.field] = action.payload.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legGroundTransportationAdded": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].groundTransportation.push({
        note: "",
      });

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legGroundTransportationDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].groundTransportation.splice(
        action.itemIndex,
        1,
      );

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    // catering

    case "legCateringFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex].catering[action.payload.itemIndex][
        action.payload.field
        ] = action.payload.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legCateringAdded": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].catering.push({
        itemId: null,
        item: null,
        itemInputValue: "",
        name: "",
        quantity: "",
        servings: "",
        notes: "",
      });

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legCateringDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].catering.splice(action.itemIndex, 1);

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    // leg notes

    case "legNotesFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex].notes[action.payload.itemIndex][
        action.payload.field
        ] = action.payload.value;
      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legNoteAdded": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].notes.push({
        note: "",
      });

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "legNoteDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.legIndex].notes.splice(action.itemIndex, 1);

      return {
        ...state,
        legs: newLegsArray,
        legsDataChanged: true,
      };
    }

    case "flightTimesDownloaded": {
      let { values } = action;

      let newLegsArray = copyObjectsArray(state.legs);

      values.forEach((item) => {
        if (item.status === "rejected" && isNaN(item?.value?.legIndex)) {
          toast.error(
            `Flight times not available for Leg ${
              item?.value?.legIndex + 1
            }, please input manually.`,
            toastOptions,
          );
          return;
        } else if (item.status === "rejected") {
          toast.error(
            `Flight times not available - please input manually.`,
            toastOptions,
          );
          return;
        }

        if (
          !item?.value?.timeWithWindAdjustment ||
          !item?.value?.status ||
          isNaN(item?.value?.legIndex)
        ) {
          toast.error(
            "Flight times not available - please input manually.",
            toastOptions,
          );
          return;
        }

        newLegsArray[item.value.legIndex].ete = timeFormatter(
          item.value.timeWithWindAdjustment,
        );
      });

      return {
        ...state,
        legs: newLegsArray,
      };
    }

    case "resetPax": {
      return {
        ...state,
        legs: state.legs?.map((leg) => ({
          ...leg,
          pax: [],
        })),
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = { ...state.unsavedChanges };

      if (!action.value) {
        delete updatedUnsavedChanges[action.field];
      } else {
        updatedUnsavedChanges[action.field] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges,
      };
    }

    default:
      break;
  }
  return state;
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const createOption = (label) => ({
  label,
  value: label,
});

const NewItineraryContent = () => {
  const history = useHistory();
  const location = useLocation();

  let { tripId, itineraryId } = useParams();

  let initialState = {
    modalPhoneNumber: "",
    selectedClient: null,
    createProspectModalIsOpen: false,
    createAuthorizedSignerModalIsOpen: false,
    routingProcessed: false,
    tripContactDetails: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      phoneCountry: "US",
    },
    createFBOModalIsOpen: false,
    isOFRRequired: true,
    showOFRCopyModal: false,
    emailInputValue: "",
    ccInputValue: "",
    bccInputValue: "",
    isFormLoading: false,
    loading: true,
    tripTag: "",

    legsDataChanged: false,
    itineraryDataChanged: false,
    initialFormValues: {},
    unsavedChanges: {},
    cateringOptions: [],
  };

  const sourcingRecords = location?.state?.sourcingRecords;

  const [state, dispatch] = useReducer(reducer, initialState);

  const setUnsavedChanges = useCallback(({ field, value }) => {
    dispatch({
      type: "unsavedChanges",
      field,
      value,
    });
  }, []);

  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [tripVerified, setTripVerified] = useState(false);
  const [tripLocked, setTripLocked] = useState(false);
  const superAdmin = (user?.["https://new.evojets.com/roles"] || []).some(
    (role) => role === "Super Admin",
  );
  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;
  useRedirectIfVerified(tripVerified, superAdmin, tripId, tripLocked, verificationAdmin);

  const processSourcing = (sourcingRecords) => {
    sourcingRecords.forEach((item) => {
      if (item.operatorPrice) {
        dispatch({
          type: "fieldChanged",
          field: "flightCharges",
          value: item.operatorPrice,
        });
      }
      if (item?.fet?.amount && !isNaN(item?.fet?.amount)) {
        dispatch({
          type: "fieldChanged",
          field: "FET",
          value: item.fet.amount.toFixed(2),
        });
      }

      if (item?.segmentFees?.amount && !isNaN(item?.segmentFees?.amount)) {
        dispatch({
          type: "fieldChanged",
          field: "segmentFees",
          value: item.segmentFees.amount.toFixed(2),
        });
      }

      if (item?.ccFee) {
        dispatch({
          type: "fieldChanged",
          field: "ccFee",
          value: item.ccFee,
        });
      }

      if (item?.paymentType) {
        dispatch({
          type: "fieldChanged",
          field: "paymentType",
          value: item.paymentType,
        });
      }
    });
  };

  const prepareNewItinerary = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let tripResponse = await ai.auth(token).get(`/api/trips/${tripId}`).catch((error) => {
      console.error(error);
      Sentry.captureException(error);
    });

    if (tripResponse && tripResponse.data) {
      dispatch({
        type: "fieldChanged",
        field: "tripTag",
        value: tripResponse.data.tripTag,
      });
      setTripVerified(!!tripResponse.data.verified);
      setTripLocked(!!tripResponse.data.lockState?.locked);
    }

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/itineraries/new`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (response && response.data) {
      dispatch({
        type: "fieldChanged",
        field: "itineraryData",
        value: response.data,
        setToInitial: true,
      });

      dispatch({ type: "fieldChanged", field: "loading", value: false });

      processRouting(response?.data?.routing, response.data?.clientData);

      parseClientData(response.data?.clientData);
    }

    if (tripResponse?.data?.owner?.id !== user?.sub) {
      dispatch({
        type: "emailOptionsFieldChanged",
        field: "ccOwner",
        value: true,
      })
    }
  };

  const prepareEditItinerary = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let tripResponse = await ai.auth(token).get(`/api/trips/${tripId}`).catch((error) => {
      console.error(error);
      Sentry.captureException(error);
    });

    if (tripResponse && tripResponse.data) {
      dispatch({
        type: "fieldChanged",
        field: "tripTag",
        value: tripResponse.data.tripTag,
      });
      setTripVerified(!!tripResponse.data.verified);
      setTripLocked(!!tripResponse.data.lockState?.locked);
    }

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/itineraries/${itineraryId}/edit`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (response && response.data) {
      dispatch({
        type: "fieldChanged",
        field: "itineraryData",
        value: response.data,
        setToInitial: true,
      });

      dispatch({ type: "fieldChanged", field: "loading", value: false });

      processExistingItinerary(response.data);
    }
  };

  const getCateringMenu = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .get(`/api/trips/cateringMenu`);

    if (response && response.data) {
      dispatch({
        type: "fieldChanged",
        field: "cateringOptions",
        value: (response.data || []).filter(({ items }) => items.length > 0).map(({ name, items }) => ({
          label: name,
          options: items.map(({ _id, name, servings, category, subCategory }) => ({
            label: name,
            value: _id,
            servings,
            category,
            subCategory,
          })),
        })),
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const processSubject = () => {
    let estimate = state?.leadData?.estimateData;

    if (!estimate?.length) {
      return;
    }

    estimate = estimate[0];

    let departDate = formatTripDate(estimate.departureDateString, "DD MMM");
    let returnDate = null;

    let subjectText = `${state?.leadData?.firstName} ${state?.leadData?.lastName} | ${departDate} | ${estimate.departureAirportCode}-${estimate.arrivalAirportCode}`;

    if (state?.leadData.tripType === 2) {
      returnDate = formatTripDate(estimate.returnDateString, "DD MMM");

      subjectText = `${state?.leadData?.firstName} ${state?.leadData?.lastName} | ${departDate} - ${returnDate} | ${estimate.departureAirportCode}-${estimate.arrivalAirportCode}`;
    }

    dispatch({
      type: "fieldChanged",
      field: "subject",
      value: subjectText,
    });
  };

  const processExistingItinerary = (itineraryData) => {
    if (itineraryData?.legs && itineraryData?.legs?.length) {
      let routing = [...itineraryData.legs];
      let finalRouting = [];

      routing?.forEach((leg) => {
        let departureFBO = null;

        if (Object.keys(leg?.departureFBO ?? {}).length) {
          departureFBO = {
            fboData: {
              ...leg?.departureFBO,
            },
            value: leg.departureFBO?._id,
            label: leg.departureFBO?.longName,
          };
        }

        let arrivalFBO = null;

        if (Object.keys(leg?.arrivalFBO ?? {}).length) {
          arrivalFBO = {
            fboData: {
              ...leg?.arrivalFBO,
            },
            value: leg.arrivalFBO?._id,
            label: leg.arrivalFBO?.longName,
          };
        }

        const formattedLeg = {
          _id: leg._id,
          aircraft: leg.aircraft,
          arrivalAirport: {
            airportName: leg.arrivalAirport?.airportName,
            id: leg.arrivalAirport?._id,
            code: leg.arrivalAirport?.code,
          },
          departureAirport: {
            airportName: leg.departureAirport?.airportName,
            id: leg.departureAirport?._id,
            code: leg.departureAirport?.code,
          },
          departureFBO,
          arrivalFBO,
          departureDate: leg.departureDate,
          passengers: leg.passengers,
          eta: leg.eta,
          etd: leg.etd,
          ete: leg.ete,
          pax: [...leg.pax].map(({ isLeadPax, isProspect, isPassenger, passengerId, ...pax }) => {
            let type = isLeadPax && !pax.firstName && !pax.lastName && !pax.middleName ? EMPTY_PASSENGER_TYPE : NEW_PASSENGER_TYPE;

            if (isProspect && itineraryData.clientData) {
              type = getProspectPassengerType(itineraryData.clientData);
            }

            if (isPassenger && passengerId) {
              const savedPassenger = itineraryData.clientData.passengers?.find(({ _id }) => _id === passengerId);

              if (savedPassenger && savedPassenger.firstName === pax.firstName && savedPassenger.middleName === pax.middleName && savedPassenger.lastName === pax.lastName) {
                type = getSavedPassengerType(savedPassenger);
              }
            }

            return {
              type,
              save: false,
              firstName: "",
              lastName: "",
              middleName: "",
              ...pax,
              dateOfBirth: pax.dateOfBirth ? new Date(pax.dateOfBirth) : null,
            };
          }),
          crew: [...leg.crew],
          groundTransportation: leg.groundTransportation || [],
          catering: (leg.catering || []).map((cateringItem) => ({
            ...cateringItem,
            item: cateringItem.itemId || cateringItem.name ? {
              label: cateringItem.name || "",
              value: cateringItem.itemId || null,
            } : null,
            itemInputValue: "",
          })),
          notes: leg.notes || [
            {
              note: "",
            },
          ],
          tailNumber: leg.tailNumber,
          ofrId: leg.ofrId,
        };

        if (leg.crew) {
          formattedLeg.crew = leg.crew.map((item) => {
            return {
              ...item,
              position: {
                label: item.position,
                value: item.position,
              },
            };
          });
        }

        if (leg.operator) {
          formattedLeg.selectedOperator = {
            id: leg.operator.id,
            name: leg.operator.name,
            label: leg.operator.name,
            value: leg.operator.id,
          };
        }
        if (leg.ofrId) {
          const ofr = (itineraryData?.operatorFinancialRecords || []).find(
            (ofr) => ofr._id === leg.ofrId,
          );
          if (ofr) {
            formattedLeg.ofr = {
              value: ofr._id,
              label: formatOFROptionLabel(ofr),
              operator: ofr.operator,
              aircraft: ofr.aircraft,
              tailNumber: ofr.tailNumber,
            };
          }
        }

        finalRouting.push(formattedLeg);
      });

      dispatch({
        type: "fieldChanged",
        field: "legs",
        value: finalRouting,
        setToInitial: true,
      });
    }

    if (itineraryData?.clientData) {
      parseClientData(itineraryData.clientData);
    }
  };

  const processRouting = (routing, clientData) => {
    let finalRouting = [];

    routing?.forEach((leg) => {
      let formattedLeg = {
        aircraft: leg.aircraft,
        arrivalAirport: leg.arrivalAirport,
        departureAirport: leg.departureAirport,
        departureDate: leg.departureDate,
        passengers: leg.passengers,
        eta: leg.eta,
        etd: leg.etd,
        ete: leg.ete,
        pax: [],
        crew: [
          {
            firstName: "",
            middleName: "",
            lastName: "",
            position: { label: "Captain", value: "Captain" },
            phoneNumber: "",
            phoneCountry: "US",
          },
        ],
        groundTransportation: [
          {
            note: "",
          },
        ],
        catering: [],
        notes: [
          {
            note: "",
          },
        ],
        tailNumber: "",
        departureFBO: null,
        arrivalFBO: null,
      };

      if (leg.operator) {
        formattedLeg.selectedOperator = {
          id: leg.operator.id,
          name: leg.operator.name,
          label: leg.operator.name,
          value: leg.operator.id,
        };
      }

      finalRouting.push(formattedLeg);
    });

    dispatch({
      type: "fieldChanged",
      field: "legs",
      value: finalRouting,
      setToInitial: true,
    });
  };

  useEffect(() => {
    handleFlightTimeCalculation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.routingProcessed]);

  const parseAuthorizedSigners = (clientData) => {
    if (!clientData?.additionalUsers?.length) {
      return false;
    }

    let authorizedSigners = clientData.additionalUsers.flatMap((item) => {
      if (!item?.isAuthorizedSigner) {
        return [];
      }
      let label = `${item.firstName} ${item.lastName} - ${item.relationship}`;
      let value = item?._id;
      return {
        ...item,
        label,
        value,
      };
    });

    dispatch({
      type: "fieldChanged",
      field: "authorizedSigners",
      value: authorizedSigners,
    });
  };

  const parseClientData = (clientData) => {
    if (!clientData) {
      //null
      return false;
    }

    if (!clientData?._id || !clientData?.firstName || !clientData?.lastName) {
      //no contact info
      return false;
    }

    if (clientData?.isBusiness && !clientData?.companyName) {
      //client is business but missing company name
      return false;
    }

    let label = `${clientData.firstName} ${clientData?.lastName} - ${clientData?.emails[0]} - ${clientData?.assigneeName}`;
    let value = clientData?._id;

    if (clientData?.isBusiness) {
      label = clientData.companyName;
    }

    parseAuthorizedSigners(clientData);

    if (clientData?.emails?.length) {
      dispatch({
        type: "fieldChanged",
        field: "email",
        value: clientData.emails[0],
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "tripContactDetails",
      value: {
        firstName: clientData?.firstName,
        lastName: clientData?.lastName,
        email: clientData?.emails[0],
        phoneNumber: clientData?.phone,
        phoneCountry: clientData?.phoneCountry || "US",
      },
    });

    const selectedClient = {
      label,
      value,
      ...clientData,
    };

    dispatch({
      type: "fieldChanged",
      field: "selectedClient",
      value: selectedClient,
      setToInitial: true,
    });
  };

  useEffect(() => {
    getCateringMenu();
  }, [getCateringMenu]);

  useEffect(() => {
    if (!itineraryId) {
      prepareNewItinerary();
    } else {
      prepareEditItinerary();
    }

    document.title = "Itinerary Builder | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId]);

  const handleAddLeg = (event) => {
    event.preventDefault();

    dispatch({ type: "legAdded" });
  };

  const loadAirportOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&includeTZ=true`);

    let options = [];
    if (data.length >= 1) {
      options = data.map((i) => ({
        value: i._id,
        label: i.airportName,
        code: i.code,
        city: i.city,
        localCode: i.localCode,
        lat: i.lat,
        lon: i.lon,
        tz: i.tz,
      }));
    }

    return options;
  };
  const loadAircraftOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/aircrafts/search?q=${inputValue}`, {
        params: {
          query: inputValue,
          page: 1,
          countPerPage: 50,
        },
      });

    if (data.aircraft.length >= 1) {
      return data.aircraft.map((aircraft) => {
        return {
          value: aircraft._id,
          label: aircraft.name,
          id: aircraft._id,
          yom: aircraft.yom,
          name: aircraft.name,
          opi: aircraft.opi,
          seats: aircraft.seats,
          speed: aircraft.cruiseSpeed,
        };
      });
    } else {
      return [];
    }
  };

  const handleClientChange = (selectedClient) => {
    setUnsavedChanges({
      field: "selectedClient",
      value: selectedClient?._id !== state.initialFormValues.selectedClient?._id,
    });

    if (selectedClient) {
      dispatch({
        type: "fieldChanged",
        field: "selectedClient",
        value: selectedClient,
      });
      dispatch({
        type: "fieldChanged",
        field: "tripContactDetails",
        value: {
          firstName: selectedClient?.firstName,
          lastName: selectedClient?.lastName,
          email: selectedClient?.emails[0],
          phoneNumber: selectedClient?.phone,
          phoneCountry: selectedClient?.phoneCountry || "US",
        },
      });
    } else {
      dispatch({
        type: "fieldChanged",
        field: "selectedClient",
        value: selectedClient,
      });
    }
    dispatch({
      type: "resetPax",
    });
  };

  const handleClientOptionCreation = (inputValue) => {
    dispatch({
      type: "fieldChanged",
      field: "createProspectModalIsOpen",
      value: true,
    });
  };

  useEffect(() => {
    setUnsavedChanges({
      field: "createProspectModal",
      value: state.createProspectModalIsOpen,
    });
  }, [setUnsavedChanges, state.createProspectModalIsOpen]);

  const handleFBOCreationRequest = (inputValue) => {
    dispatch({
      type: "fieldChanged",
      field: "createFBOModalIsOpen",
      value: true,
    });
  };

  useEffect(() => {
    setUnsavedChanges({
      field: "createFBOModal",
      value: state.createFBOModalIsOpen,
    });
  }, [setUnsavedChanges, state.createFBOModalIsOpen]);

  const handleItinerarySubmit = async (e) => {
    e.preventDefault();

    if (state.isFormLoading) {
      return;
    }

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    if (!state.selectedClient?._id) {
      return toast.error(
        "Please select client and retry.",
        toastOptions,
      );
    }

    if (state.isOFRRequired && !!state.legs.find(({ ofrId }) => !ofrId)) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    let isPassengersDataValid = true;
    state.legs.forEach((leg) => {
      leg.pax.forEach((pax, ) => {
        if (
          pax.type?.value === "new" && (
            !pax.firstName ||
            !pax.lastName ||
            (pax.dateOfBirth && (moment(pax.dateOfBirth).isAfter(moment(), "day") || moment(pax.dateOfBirth).isBefore(moment('1900-01-01'), "day"))) ||
            (pax.weight && (pax.weight < 2 || pax.weight > 660 || !/^-?\d+(\.\d{1})?$/.test(pax.weight)))
          )
        ) {
          isPassengersDataValid = false;
        }
      });
    });
    if (!isPassengersDataValid) {
      return toast.error(
        "Please fill in all required passengers fields and retry.",
        toastOptions,
      );
    }

    let isCateringDataValid = true;
    state.legs.forEach((leg) => {
      (leg.catering || []).forEach((item) => {
        if (
          !item.name ||
          !item.quantity ||
          item.quantity < 1
        ) {
          isCateringDataValid = false;
        }
      });
    });
    if (!isCateringDataValid) {
      return toast.error(
        "Please fill in all required catering fields and retry.",
        toastOptions,
      );
    }

    let legs = state.legs.map(
      ({
        _id,
        departureDate,
        departureAirport,
        arrivalAirport,
        passengers,
        etd,
        eta,
        ete,
        aircraft,
        selectedOperator,
        arrivalTimeZone,
        departureTimeZone,
        tailNumber,
        departureFBO,
        arrivalFBO,
        pax,
        crew,
        catering,
        groundTransportation,
        notes,
        ofrId,
      }) => {
        let operator = {
          id: selectedOperator?.id,
          name: selectedOperator?.operatorName || selectedOperator?.name,
        };

        if (
          selectedOperator?.value &&
          selectedOperator?.label &&
          selectedOperator?.value === selectedOperator?.label
        ) {
          operator = {
            id: null,
            name: selectedOperator?.value,
          };
        }

        crew = (crew || []).map((item) => {
          return {
            firstName: item.firstName,
            middleName: item.middleName,
            lastName: item.lastName,
            position: item?.position?.value,
            phoneNumber: item.phoneNumber,
            phoneCountry: item.phoneCountry,
          };
        });

        pax = pax?.map((item, index) => ({
          firstName:
            item.type.value === "new" ? item.firstName : item.type.firstName,
          middleName:
            item.type.value === "new" ? item.middleName : item.type.middleName,
          lastName:
            item.type.value === "new" ? item.lastName : item.type.lastName,
          dateOfBirth:
            item.type.value === "new" ? item.dateOfBirth : item.type.dateOfBirth,
          weight:
            item.type.value === "new" ? item.weight : item.type.weight,
          gender:
            item.type.value === "new" ? item.gender : item.type.gender,
          isLeadPax: index === 0,
          isProspect: item.type.value === "prospect",
          isPassenger: !["prospect", "new"].includes(item.type.value),
          passengerId: !["prospect", "new"].includes(item.type.value)
            ? item.type.value
            : null,
          save: item.type.value === "new" && item.save,
        }));

        departureFBO = {
          id: departureFBO?.value,
          name: departureFBO?.fboData?.longName || departureFBO?.label,
        };

        arrivalFBO = {
          id: arrivalFBO?.value,
          name: arrivalFBO?.fboData?.longName || arrivalFBO?.label,
        };

        return {
          _id,
          departureDate,
          departureAirport,
          arrivalAirport,
          passengers,
          etd,
          eta,
          ete,
          aircraft,
          arrivalTimeZone,
          departureTimeZone,
          operator,
          tailNumber,
          departureFBO,
          arrivalFBO,
          pax,
          crew,
          catering: (catering || []).map(({ _id, itemId, name, quantity, servings, notes }) => ({
            _id,
            itemId,
            name,
            quantity: parseInt(quantity) || "",
            servings,
            notes,
          })),
          groundTransportation: groundTransportation || [],
          notes: notes || [],
          ofrId,
        };
      },
    );

    let { tripContactDetails } = state;
    const { emailOptions = {} } = state.itineraryData;

    let itineraryData = {
      clientId: state.selectedClient?._id,
      tripContactDetails,
      legs,
      emailOptions: {
        ...(emailOptions || {}),
        email: (emailOptions?.email || []).map(({ value }) => value),
        cc: (emailOptions?.cc || []).map(({ value }) => value),
        bcc: (emailOptions?.bcc || []).map(({ value }) => value),
      },
    };

    dispatch({
      type: "fieldChanged",
      field: "isFormLoading",
      value: true,
    });

    let response = false;

    try {
      if (!itineraryId) {
        //create
        response = await ai
          .auth(token)
          .post(`/api/trips/${tripId}/itineraries/create`, itineraryData)
          .catch((error) => {
            console.error(error);
            Sentry.captureException(error);
            toast.error(
              "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports, passenger first and last name for all legs, email options.",
              toastOptions,
            );
          });
      } else {
        //edit
        response = await ai
          .auth(token)
          .put(`/api/trips/${tripId}/itineraries/${itineraryId}`, itineraryData)
          .catch((error) => {
            console.error(error);
            Sentry.captureException(error);
            toast.error(
              "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports, passenger first and last name for all legs, email options.",
              toastOptions,
            );
          });
      }
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "isFormLoading",
        value: false,
      });
    }

    if (response && response.data) {
      dispatch({
        type: "fieldChanged",
        field: "unsavedChanges",
        value: {},
      });

      setTimeout(() => {
        toast.success("Itinerary saved successfully!", toastOptions);
      }, 500);

      history.push(
        `/trips/${tripId}/itineraries/${response.data?._id}/preview`,
      );
    }
  };

  const handleAirportChange = async (legIndex, airport, airportIndex) => {
    let legData = { ...state.legs[legIndex] };

    if (airportIndex === 0) {
      legData.departureAirport = {
        id: airport.value,
        name: airport.label,
      };

      let departureTimeZone = null;
      if (airport.tz && airport.tz.length) {
        departureTimeZone = airport.tz[0];
      }

      if (departureTimeZone?.length) {
        dispatch({
          type: "legFieldChanged",
          payload: {
            legIndex: legIndex,
            field: "departureTimeZone",
            value: departureTimeZone,
          },
        });
      }
    }

    if (airportIndex === 1) {
      legData.arrivalAirport = {
        id: airport.value,
        name: airport.label,
      };

      let arrivalTimeZone = null;

      if (airport.tz && airport.tz.length) {
        arrivalTimeZone = airport.tz[0];
      }

      if (arrivalTimeZone?.length) {
        dispatch({
          type: "legFieldChanged",
          payload: {
            legIndex: legIndex,
            field: "arrivalTimeZone",
            value: arrivalTimeZone,
          },
        });
      }
    }

    await handleFlightTimeCalculationByIndex(legData, legIndex);
  };

  const handleAircraftChange = async (legIndex, updatedAircraft) => {
    // airportIndex = 0 = depart
    // airportIndex = 1 = arrive

    let legData = { ...state.legs[legIndex] };

    legData.aircraft = updatedAircraft;

    await handleFlightTimeCalculationByIndex(legData, legIndex);
  };

  const handleFlightTimeCalculationByIndex = async (legData, legIndex) => {
    //todo - update syntax
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let params = {
      legData: {
        arrivalAirportId: legData.arrivalAirport.id,
        departureAirportId: legData.departureAirport.id,
        legIndex: legIndex,
        aircraftId: legData.aircraft.id,
      },
    };

    let response = await ai
      .auth(token)
      .post(`/api/trips/flightTimes`, params)
      .catch((err) => {
        console.error(err.response.data);
        toast.error(
          "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
          toastOptions,
        );
        return;
      });

    if (
      response &&
      response?.data &&
      response?.data?.length &&
      response?.data[0]?.value
    ) {
      let returnedValueForLeg = response?.data[0]?.value;

      let composedString = "";

      if (returnedValueForLeg?.timeWithWindAdjustment) {
        composedString = timeFormatter(
          returnedValueForLeg?.timeWithWindAdjustment,
        );
      }

      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex,
          field: "ete",
          value: composedString,
        },
      });

      handleETEChange(legIndex, composedString);

      return toast.success(`ETE and ETA for Leg ${legIndex + 1} were updated.`);
    }
  };

  const calculateETAForLeg = async (ete, etd, leg) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let etaFactors = {
      ete,
      etd,
      leg,
    };

    // re-calculate ETA

    let response = await ai
      .auth(token)
      .post(`/api/trips/calculateETAOneLeg`, etaFactors)
      .catch((err) => {
        console.error(err.response.data);
        return;
      });

    return response;
  };

  const handleETDChange = async (legIndex, updatedETD) => {
    let leg = { ...state.legs[legIndex] };

    let pattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    const isValid = pattern.test(leg.ete);

    if (!isValid && leg.ete !== "" && leg.ete !== null) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex: legIndex,
          field: "ete",
          value: "",
        },
      });

      return toast.error(
        `Please enter a valid ETE for Leg ${
          legIndex + 1
        } to recalculate the ETA.`,
        toastOptions,
      );
    }

    let etaResponse = await calculateETAForLeg(leg.ete, updatedETD, leg);
    if (!etaResponse.data) {
      return;
    }

    if (etaResponse.data?.eta) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "eta",
          value: etaResponse?.data?.eta,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.departureTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "departureTimeZone",
          value: etaResponse?.data?.departureTimeZone,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.arrivalTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "arrivalTimeZone",
          value: etaResponse?.data?.arrivalTimeZone,
          legIndex,
        },
      });
    }
  };

  const handleETEChange = async (legIndex, updatedETE) => {
    let leg = { ...state.legs[legIndex] };

    let pattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

    const isValid = pattern.test(updatedETE);

    if (!isValid) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex: legIndex,
          field: "ete",
          value: "",
        },
      });

      return toast.error(
        `Please enter a valid ETE for Leg ${legIndex + 1}`,
        toastOptions,
      );
    }

    if (!state.legs[legIndex]?.etd) {
      return toast.error(
        `Please enter a valid ETD for Leg ${
          legIndex + 1
        } to recalculate the ETA.`,
        toastOptions,
      );
    }

    let etaResponse = await calculateETAForLeg(
      updatedETE,
      state.legs[legIndex]?.etd,
      leg,
    );

    dispatch({
      type: "legFieldChanged",
      payload: {
        field: "eta",
        value: etaResponse.data?.eta,
        legIndex,
      },
    });

    if (etaResponse.data?.departureTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "departureTimeZone",
          value: etaResponse.data?.departureTimeZone,
          legIndex,
        },
      });
    }

    if (etaResponse.data?.arrivalTimeZone) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          field: "arrivalTimeZone",
          value: etaResponse.data?.arrivalTimeZone,
          legIndex,
        },
      });
    }
  };

  const handleFlightTimeCalculation = async (legData) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    if (!state.routingProcessed) {
      return;
    }

    let params = {
      legData: state.legs.flatMap((item, index) => {
        if (!item?.aircraft?.id) {
          return [];
        }

        return {
          arrivalAirportId: item.arrivalAirport.id,
          departureAirportId: item.departureAirport.id,
          legIndex: index,
          aircraftId: item?.aircraft?.id,
        };
      }),
    };

    if (params?.legData?.length === 0) {
      return;
    }

    let response = await ai
      .auth(token)
      .post(`/api/trips/flightTimesMulti`, params)
      .catch((err) => {
        console.error(err.response.data);
        toast.error(
          "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
          toastOptions,
        );
        return;
      });

    if (response && response.data) {
      dispatch({
        type: "flightTimesDownloaded",
        values: response.data,
      });
      return response.data;
    }
  };

  const handleOperatorChange = (selectedOperator, legIndex) => {
    if (selectedOperator) {
      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex: legIndex,
          field: "selectedOperator",
          value: selectedOperator,
        },
      });
    } else {
      resetSelection("selectedOperator", legIndex);
    }

    dispatch({
      type: "fieldChanged",
      field: "legsDataChanged",
      value: true,
    });
  };

  const handleOFRChange = (selectedValue, legIndex) => {
    if (
      legIndex === 0 &&
      state.legs?.length > 1 &&
      selectedValue?.value !== state.legs[0].ofrId
    ) {
      dispatch({
        type: "fieldChanged",
        field: "showOFRCopyModal",
        value: true,
      });
    }
    dispatch({
      type: "legFieldChanged",
      payload: {
        legIndex,
        field: "ofr",
        value: selectedValue,
      },
    });
  };

  const handleConfirmOFRCopy = () => {
    dispatch({ type: "legOFRCopy" });
    dispatch({
      type: "fieldChanged",
      field: "showOFRCopyModal",
      value: false,
    });
  };

  const handleCloseOFRCopyModal = () => {
    dispatch({
      type: "fieldChanged",
      field: "showOFRCopyModal",
      value: false,
    });
  };

  const resetSelection = (fieldName, legIndex) => {
    setTimeout(() => {
      dispatch({
        type: "legFieldChanged",
        payload: {
          legIndex: legIndex,
          field: "selectedOperator",
          value: null,
        },
      });
    }, 0);
  };

  const handleDepartureDateChange = async (legIndex, updatedDepartureDate) => {
    let leg = { ...state.legs[legIndex] };

    // departureDate is a date-only marker at UTC midnight, so read its parts in
    // UTC. Reading them locally would re-anchor the ETD to the previous day for
    // anyone west of UTC.
    const updatedDay = moment.utc(updatedDepartureDate);

    const updatedETD = moment
      .tz(leg.etd, leg.departureTimeZone) // Start with original ETD
      .set({
        year: updatedDay.year(),
        month: updatedDay.month(),
        date: updatedDay.date(),
      })
      .toISOString();

    // Update the ETD field with the new date + existing time
    dispatch({
      type: "legFieldChanged",
      payload: {
        field: "etd",
        value: updatedETD,
        legIndex,
      },
    });
  };

  const handleMatchPax = (e) => {
    e.preventDefault();
    e.stopPropagation();

    //get pax from leg 1
    //replace pax in all other legs with same pax

    let pax = state.legs[0]?.pax;

    if (!pax?.length) {
      return;
    }

    let updatedLegs = state.legs.map((leg, index) => {
      if (index === 0) {
        return leg;
      }

      return {
        ...leg,
        pax,
      };
    });

    dispatch({
      type: "fieldChanged",
      field: "legs",
      value: updatedLegs,
    });
  };

  useEffect(() => {
    if (document) {
      document.getElementById("page-heading").scrollIntoView({
        behavior: "smooth",
      });
    }

    if (sourcingRecords?.length) {
      processSourcing(sourcingRecords);
    }

    processSubject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading, sourcingRecords]);

  const passengerOptions = useMemo(
    () =>
      [
        NEW_PASSENGER_TYPE,
        state.selectedClient
          ? getProspectPassengerType(state.selectedClient)
          : null,
        (state.selectedClient?.passengers || [])
          .map((savedPassenger) => getSavedPassengerType(savedPassenger))
          .sort((a, b) => a.firstName?.localeCompare(b.firstName || "")),
      ]
        .flat()
        .filter(Boolean),
    [state.selectedClient],
  );

  const leadPassengerOptions = useMemo(
    () => [
      {
        value: null,
        label: "N/A",
      },
      ...passengerOptions,
    ],
    [passengerOptions],
  );

  const ofrOptions = useMemo(
    () =>
      (state.itineraryData?.operatorFinancialRecords || []).map((ofr) => ({
        value: ofr._id,
        label: formatOFROptionLabel(ofr),
        operator: ofr.operator,
        aircraft: ofr.aircraft,
        tailNumber: ofr.tailNumber,
      })),
    [state.itineraryData],
  );

  const handleEmailOptionsTextInputChange = useCallback(
    (e) =>
      dispatch({
        type: "emailOptionsFieldChanged",
        field: e.target.name,
        value: e.target.value,
      }),
    [],
  );

  const handleEmailOptionsEmailChange = useCallback(
    (newValue) =>
      dispatch({
        type: "emailOptionsFieldChanged",
        field: "email",
        value: newValue || [],
      }),
    [],
  );

  const handleEmailOptionsCCChange = useCallback(
    (newValue) =>
      dispatch({
        type: "emailOptionsFieldChanged",
        field: "cc",
        value: newValue || [],
      }),
    [],
  );

  const handleEmailOptionsBCCChange = useCallback(
    (newValue) =>
      dispatch({
        type: "emailOptionsFieldChanged",
        field: "bcc",
        value: newValue || [],
      }),
    [],
  );

  const handleEmailOptionsCCOwnerChange = useCallback(
    (e) =>
      dispatch({
        type: "emailOptionsFieldChanged",
        field: "ccOwner",
        value: e.target.checked,
      }),
    [],
  );

  const handleEmailOptionsBCCSenderChange = useCallback(
    (e) =>
      dispatch({
        type: "emailOptionsFieldChanged",
        field: "bccSender",
        value: e.target.checked,
      }),
    [],
  );

  const handleEmailInputChange = useCallback(
    (inputValue, action) => {
      dispatch({
        type: "fieldChanged",
        field: "emailInputValue",
        value: inputValue,
      });

      setUnsavedChanges({
        field: "emailInputValue",
        value: !!inputValue,
      });

      if (
        action.action === "menu-close" &&
        validateEmail(state.emailInputValue)
      ) {
        dispatch({
          type: "emailOptionsFieldChanged",
          field: "email",
          value: [
            ...(state.itineraryData?.emailOptions?.email || []),
            createOption(state.emailInputValue),
          ],
        });
        dispatch({
          type: "fieldChanged",
          field: "emailInputValue",
          value: "",
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.emailInputValue, state.itineraryData],
  );

  const handleCcInputChange = useCallback(
    (inputValue, action) => {
      dispatch({
        type: "fieldChanged",
        field: "ccInputValue",
        value: inputValue,
      });

      setUnsavedChanges({
        field: "ccInputValue",
        value: !!inputValue,
      });

      if (action.action === "menu-close" && validateEmail(state.ccInputValue)) {
        dispatch({
          type: "emailOptionsFieldChanged",
          field: "cc",
          value: [
            ...(state.itineraryData?.emailOptions?.cc || []),
            createOption(state.ccInputValue),
          ],
        });
        dispatch({
          type: "fieldChanged",
          field: "ccInputValue",
          value: "",
        });
        setUnsavedChanges({
          field: "ccInputValue",
          value: false,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.ccInputValue, state.itineraryData],
  );

  const handleBccInputChange = useCallback(
    (inputValue, action) => {
      dispatch({
        type: "fieldChanged",
        field: "bccInputValue",
        value: inputValue,
      });

      setUnsavedChanges({
        field: "bccInputValue",
        value: !!inputValue,
      });

      if (
        action.action === "menu-close" &&
        validateEmail(state.bccInputValue)
      ) {
        dispatch({
          type: "emailOptionsFieldChanged",
          field: "bcc",
          value: [
            ...(state.itineraryData?.emailOptions?.bcc || []),
            createOption(state.bccInputValue),
          ],
        });
        dispatch({
          type: "fieldChanged",
          field: "bccInputValue",
          value: "",
        });
        setUnsavedChanges({
          field: "bccInputValue",
          value: false,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.bccInputValue, state.itineraryData],
  );

  const handleEmailKeyDown = useCallback(
    (event) => {
      if (!state.emailInputValue || validateEmail(state.emailInputValue))
        return;

      switch (event.key) {
        case "Enter":
        case "Tab":
          if (state.itineraryData?.emailOptions?.email) {
            dispatch({
              type: "emailOptionsFieldChanged",
              field: "email",
              value: [
                ...(state.itineraryData?.emailOptions?.email || []),
                createOption(state.emailInputValue),
              ],
            });
          } else {
            dispatch({
              type: "emailOptionsFieldChanged",
              field: "email",
              value: [createOption(state.emailInputValue)],
            });
          }

          dispatch({
            type: "fieldChanged",
            field: "emailInputValue",
            value: "",
          });
          setUnsavedChanges({
            field: "emailInputValue",
            value: false,
          });
          event.preventDefault();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.emailInputValue, state.itineraryData],
  );

  const handleCcKeyDown = useCallback(
    (event) => {
      if (!state.ccInputValue) return;

      switch (event.key) {
        case "Enter":
        case "Tab":
          dispatch({
            type: "emailOptionsFieldChanged",
            field: "cc",
            value: [
              ...(state.itineraryData?.emailOptions?.cc || []),
              createOption(state.ccInputValue),
            ],
          });

          dispatch({
            type: "fieldChanged",
            field: "ccInputValue",
            value: "",
          });
          event.preventDefault();
      }
    },
    [state.ccInputValue, state.itineraryData],
  );

  const handleBccKeyDown = useCallback(
    (event) => {
      if (!state.bccInputValue) return;

      switch (event.key) {
        case "Enter":
        case "Tab":
          dispatch({
            type: "emailOptionsFieldChanged",
            field: "bcc",
            value: [
              ...(state.itineraryData?.emailOptions?.bcc || []),
              createOption(state.bccInputValue),
            ],
          });

          dispatch({
            type: "fieldChanged",
            field: "bccInputValue",
            value: "",
          });
          event.preventDefault();
      }
    },
    [state.bccInputValue, state.itineraryData],
  );

  useEffect(() => {
    if (state.legsDataChanged) {
      // not processing fields
      // departureAirport
      // arrivalAirport
      // servings - on edit page
      // eta
      // etd
      // ete

      const arr1 = state.legs?.map(({ departureTimeZone, arrivalTimeZone, eta, etd, ete, ...item }) => item);

      const arr2 = state.initialFormValues.legs?.map(({ eta, etd, ete, ...item }) => item);

      setUnsavedChanges({
        field: "legsData",
        value: !isEqual(arr1, arr2),
      });

      dispatch({
        type: "fieldChanged",
        field: "legsDataChanged",
        value: false,
      });
    }
  }, [
    setUnsavedChanges,
    state.legsDataChanged,
    state.legs,
    state.initialFormValues.legs,
  ]);

  useEffect(() => {
    if (state.itineraryDataChanged) {
      setUnsavedChanges({
        field: "itineraryData",
        value: !isEqual(state.itineraryData, state.initialFormValues.itineraryData),
      });

      dispatch({
        type: "fieldChanged",
        field: "itineraryDataChanged",
        value: false,
      });
    }
  }, [
    setUnsavedChanges,
    state.itineraryDataChanged,
    state.itineraryData,
    state.initialFormValues.itineraryData,
  ]);

  const filterCateringOption = useCallback(
    ({ label, data: { category, subCategory } }, input) =>
      label.toLowerCase().includes(input.toLowerCase()) ||
      category?.toLowerCase()?.includes(input.toLowerCase()) ||
      subCategory?.toLowerCase()?.includes(input.toLowerCase()),
    [],
  );

  return (
    <Fragment>
      <div id="page-heading">
        {!state.loading && <PageHeader
          pageTitle={itineraryId ? "Edit Itinerary" : "New Itinerary"}
          breadcrumbs={[
            { link: "/trips", label: "Trips" },
            { link: `/trips/${tripId}`, label: `Trip #${state.tripTag}` },
            { label: itineraryId ? "Edit Itinerary" : "New Itinerary", isCurrentPage: true },
          ]}
          goBackLink={`/trips/${tripId}`}
        />}
      </div>

      <form
        id="new-itinerary"
        onSubmit={handleItinerarySubmit}
        className="base-form"
      >
        <ProspectsAutocomplete
          onCreateOption={handleClientOptionCreation}
          handleProspectChange={handleClientChange}
          prospectOptions={[]}
          parentDispatch={dispatch}
          selectedProspect={state.selectedClient}
          label="Client"
          placeholder="Select a Client"
        />

        <CreateProspectModal
          modalIsOpen={state.createProspectModalIsOpen}
          parentDispatch={dispatch}
          toast={toast}
          defaultPhoneNumber={state.modalPhoneNumber}
          toastOptions={toastOptions}
        />

        <CreateFBOModal
          modalIsOpen={state.createFBOModalIsOpen}
          parentDispatch={dispatch}
          toast={toast}
          toastOptions={toastOptions}
          modalStateKey={`createFBOModalIsOpen`}
        />

        <div className="flight-details-container">
          <h5>Routing</h5>

          {state?.legs?.length > 0 &&
            state?.legs?.map((leg, index) => (
              <div className="flight-details-block" key={index}>
                <div className="flex-row column-gap-1pct">
                  <div className="form-group flex-grow-1">
                    <label className="base-label">
                      Apply OFR{" "}
                      {state.isOFRRequired && (
                        <span className="required">*</span>
                      )}
                    </label>

                    <Select
                      className="selectedOFR-select"
                      classNamePrefix="select"
                      options={ofrOptions}
                      placeholder="Select OFR"
                      onChange={(selectedValue) => {
                        handleOFRChange(selectedValue, index);
                      }}
                      key={`ofr[${index}]${leg?.ofrId}`}
                      value={leg?.ofr}
                      name={`ofr[${index}]`}
                      required
                    />
                  </div>

                  <OperatorAutocomplete
                    handleChange={(selectedOperator) =>
                      handleOperatorChange(selectedOperator, index)
                    }
                    defaultOperatorOptions={state.operatorOptions}
                    parentDispatch={dispatch}
                    selectedOperator={leg.selectedOperator}
                    containerClasses="flex-grow-1"
                    allowCreate
                    isDisabled={leg?.ofrId || state.isOFRRequired}
                  />

                  <div className="form-group flex-grow-1">
                    <label htmlFor={`aircraft[${index}]`}>Aircraft</label>
                    <AsyncSelect
                      name={`aircraft[${index}]`}
                      className="aircraft-select mb-0"
                      classNamePrefix="select"
                      cacheOptions
                      key={`aircraft[${index}]${leg?.aircraft?.name}`}
                      loadOptions={loadAircraftOptions}
                      placeholder="Search Aircraft"
                      defaultValue={{
                        label: leg?.aircraft?.name,
                        value: leg?.aircraft?.id,
                      }}
                      onChange={(selectedAircraft) => {
                        handleAircraftChange(index, selectedAircraft);

                        dispatch({
                          type: "legFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "aircraft",
                            value: {
                              name: selectedAircraft.name,
                              seats: selectedAircraft.seats,
                              yom: selectedAircraft.yom,
                              id: selectedAircraft.id,
                            },
                          },
                        });
                      }}
                      isDisabled={leg?.ofrId || state.isOFRRequired}
                    />
                  </div>

                  <TextInput
                    placeholder="Tail Number"
                    label="Tail Number"
                    name={`tailNumber[${index}]`}
                    handleChange={(e) => {
                      dispatch({
                        type: "legFieldChanged",
                        payload: {
                          legIndex: index,
                          field: "tailNumber",
                          value: e.target.value || "",
                        },
                      });
                    }}
                    value={leg.tailNumber}
                    className="base-input mb-0"
                    containerClasses="flex-half mb-0"
                  />
                </div>

                <div className="flex-row column-gap-1pct">
                  <div className="flex-three-quarters flex-row column-gap-2pct">
                    <div className="datepickerContainer mb-0 flex-half">
                      <label
                        htmlFor={`departureDate[${index}]`}
                        className="base-label"
                      >
                        Date <span className="required">*</span>
                      </label>

                      <Datepicker
                        value={leg.departureDate}
                        onChange={(event) => {
                          handleDepartureDateChange(index, event.value);

                          dispatch({
                            type: "legFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "departureDate",
                              value: event.value,
                            },
                          });
                        }}
                        dataTimezone="utc"
                        displayTimezone="utc"
                        timezonePlugin={momentTimezone}
                        controls={["date"]}
                      />
                    </div>

                    <div className="form-group flex-half">
                      <label htmlFor={`etd[${index}]`}>ETD</label>
                      <Datepicker
                        onChange={(e) => {
                          handleETDChange(index, e.value);

                          dispatch({
                            type: "legFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "etd",
                              value: e.value || null,
                            },
                          });
                        }}
                        timezonePlugin={momentTimezone}
                        dataTimezone="utc"
                        displayTimezone={leg.departureTimeZone}
                        controls={["time"]}
                        value={leg.etd}
                        defaultSelection="12:00 am"
                      ></Datepicker>
                    </div>

                    <div className="form-group flex-half">
                      <label htmlFor={`eta[${index}]`}>ETA</label>
                      <Datepicker
                        onChange={(e) => {
                          dispatch({
                            type: "legFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "eta",
                              value: e.value || null,
                            },
                          });
                        }}
                        timezonePlugin={momentTimezone}
                        dataTimezone="utc"
                        displayTimezone={leg.arrivalTimeZone}
                        controls={["time"]}
                        value={leg.eta}
                        defaultSelection="12:00 am"
                      ></Datepicker>
                    </div>
                  </div>

                  <div className="airportContainer flex-one mb-0">
                    <label
                      htmlFor={`departureAirport[${index}]`}
                      className="base-label"
                    >
                      Depart
                    </label>

                    <AsyncSelect
                      key={`departure-${leg.departureAirport?.airportName}`}
                      name={`departureAirport[${index}]`}
                      className="departure-airport-select mb-0"
                      classNamePrefix="select"
                      cacheOptions
                      loadOptions={loadAirportOptions}
                      placeholder="Search Airports"
                      defaultValue={{
                        label: leg?.departureAirport?.airportName,
                        value: leg?.departureAirport?.id,
                      }}
                      onChange={(selectedAirport) => {
                        handleAirportChange(index, selectedAirport, 0);

                        dispatch({
                          type: "legFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "departureAirport",
                            value: {
                              airportName: selectedAirport?.label || "",
                              id: selectedAirport?.value || "",
                              code: selectedAirport?.code || "",
                              city: selectedAirport?.city || "",
                              localCode: selectedAirport?.localCode || "",
                            },
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="airportContainer flex-one">
                    <label
                      htmlFor={`departureAirport[${index}]`}
                      className="base-label"
                    >
                      Arrive
                    </label>

                    <AsyncSelect
                      key={`arrival-${leg.arrivalAirport.airportName}`}
                      name={`arrivalAirport[${index}]`}
                      className="arrival-airport-select mb-0"
                      classNamePrefix="select"
                      cacheOptions
                      loadOptions={loadAirportOptions}
                      placeholder="Search Airports"
                      defaultValue={{
                        label: leg.arrivalAirport.airportName,
                        value: leg.arrivalAirport.id,
                      }}
                      onChange={(selectedAirport) => {
                        handleAirportChange(index, selectedAirport, 1);

                        dispatch({
                          type: "legFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "arrivalAirport",
                            value: {
                              airportName: selectedAirport?.label || "",
                              id: selectedAirport?.value || "",
                              code: selectedAirport?.code || "",
                              city: selectedAirport?.city || "",
                              localCode: selectedAirport?.localCode || "",
                            },
                          },
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="flex-row column-gap-1pct">
                  <div className="flex-three-quarters flex-row column-gap-2pct">
                    <TextInput
                      name={`ete[${index}]`}
                      label="ETE"
                      handleChange={(e) => {
                        dispatch({
                          type: "legFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "ete",
                            value: e.target.value || "",
                          },
                        });
                      }}
                      handleBlur={(e) => {
                        handleETEChange(index, e.target.value);
                      }}
                      className="base-input"
                      containerClasses="flex-half mb-0"
                      value={leg.ete}
                    />

                    <TextInput
                      type={"number"}
                      name={`passengers[${index}]`}
                      label="PAX"
                      handleChange={(e) => {
                        dispatch({
                          type: "legFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "passengers",
                            value: parseInt(e.target.value) || "",
                          },
                        });
                      }}
                      className="base-input"
                      containerClasses="flex-half mb-0"
                      value={leg.passengers}
                      required
                    />
                  </div>

                  <FBOAutocomplete
                    placeholder="Search FBOs"
                    label="DEPARTURE FBO"
                    selectedFBO={leg?.departureFBO}
                    airports={[leg?.departureAirport]}
                    handleChange={(selectedFBO) => {
                      dispatch({
                        type: "legFieldChanged",
                        payload: {
                          legIndex: index,
                          field: "departureFBO",
                          value: selectedFBO,
                        },
                      });
                    }}
                    allowCreate
                    handleCreateOption={handleFBOCreationRequest}
                    containerClasses="flex-one"
                  />

                  <FBOAutocomplete
                    placeholder="Search FBOs"
                    label="ARRIVAL FBO"
                    selectedFBO={leg?.arrivalFBO}
                    airports={[leg?.arrivalAirport]}
                    handleChange={(selectedFBO, action) => {
                      dispatch({
                        type: "legFieldChanged",
                        payload: {
                          legIndex: index,
                          field: "arrivalFBO",
                          value: selectedFBO,
                        },
                      });
                    }}
                    allowCreate
                    handleCreateOption={handleFBOCreationRequest}
                    containerClasses="flex-one"
                  />
                </div>

                <hr className="mt-5 mb-20"></hr>

                <h5 className="d-flex">
                  Passengers

                  <button
                    className="base-button icon-button icon-button-small evo-dark-green ml-2"
                    title="Add Passenger"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({
                        type: "paxAdded",
                        legIndex: index,
                      });
                    }}
                  >
                    <span className="material-icons">add_circle</span>
                  </button>

                  {index === 0 && state?.legs?.length > 1 && (
                    <button
                      className="base-button icon-button icon-button-small evo-blue ml-2"
                      title="Sync Pax To All Legs"
                      onClick={handleMatchPax}
                    >
                      <span className="material-icons">sync</span>
                    </button>
                  )}
                </h5>

                {!leg?.pax?.length && (
                  <div>No passengers added yet</div>
                )}

                {leg?.pax?.map((passenger, paxIndex) => (
                  <div key={passenger._id || paxIndex}
                       className="flex-row column-gap-1pct flex-align-center itinerary-passenger-row">
                    <div className="form-group mb-20 flex-half">
                      <label>
                        {paxIndex === 0
                          ? "Lead Passenger"
                          : `Passenger ${paxIndex + 1}`}
                      </label>

                      <Select
                        key={`passenger-type-${paxIndex}-options`}
                        classNamePrefix="select"
                        options={
                          (paxIndex === 0 ? leadPassengerOptions : passengerOptions)
                            .filter(
                              ({ value }) =>
                                !leg?.pax
                                  .filter(
                                    ({ type }, index) =>
                                      index !== paxIndex &&
                                      type.value !== "new" &&
                                      type.value !== passenger.type.value,
                                  )
                                  .map(({ type }) => type.value)
                                  .includes(value),
                            )}
                        name={`pax[type][${index}]`}
                        value={passenger.type}
                        placeholder="Passenger"
                        onChange={(selectedType) => {
                          dispatch({
                            type: "legPaxFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "type",
                              value: selectedType,
                              paxIndex: paxIndex,
                            },
                          });
                        }}
                      />
                    </div>

                    {passenger.type?.value === "new" && (
                      <>
                        <TextInput
                          type={"text"}
                          name={`pax[firstName][${index}]`}
                          label="First Name"
                          required
                          handleChange={(e) => {
                            dispatch({
                              type: "legPaxFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "firstName",
                                value: e.target.value || "",
                                paxIndex: paxIndex,
                              },
                            });
                          }}
                          className="base-input mb-0"
                          containerClasses="flex-half mb-0"
                          value={passenger.firstName}
                        />

                        <TextInput
                          type={"text"}
                          name={`middleName[${index}]`}
                          label="Middle Name"
                          handleChange={(e) => {
                            dispatch({
                              type: "legPaxFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "middleName",
                                value: e.target.value || "",
                                paxIndex: paxIndex,
                              },
                            });
                          }}
                          className="base-input mb-0"
                          containerClasses="flex-half mb-0"
                          value={passenger.middleName}
                        />

                        <TextInput
                          type={"text"}
                          name={`lastName[${index}]`}
                          label="Last Name"
                          required
                          handleChange={(e) => {
                            dispatch({
                              type: "legPaxFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "lastName",
                                value: e.target.value || "",
                                paxIndex: paxIndex,
                              },
                            });
                          }}
                          className="base-input mb-0"
                          containerClasses="flex-half mb-0"
                          value={passenger.lastName}
                        />

                        <div className="form-group mb-20 flex-half">
                          <label>
                            Date of Birth
                          </label>

                          <DatePicker
                            name={`dateOfBirth[${index}]`}
                            onChange={(date) => {
                              dispatch({
                                type: "legPaxFieldChanged",
                                payload: {
                                  legIndex: index,
                                  field: "dateOfBirth",
                                  value: date,
                                  paxIndex: paxIndex,
                                },
                              });
                            }}
                            value={passenger.dateOfBirth}
                          />
                        </div>

                        <TextInput
                          name={`weight[${index}]`}
                          label="Weight (lb)"
                          handleChange={(e) => {
                            dispatch({
                              type: "legPaxFieldChanged",
                              payload: {
                                legIndex: index,
                                field: "weight",
                                value: e.target.value || "",
                                paxIndex: paxIndex,
                              },
                            });
                          }}
                          value={passenger.weight}
                          type="number"
                          step="0.1"
                          min="2"
                          max="660"
                          className="base-input mb-0"
                          containerClasses="flex-half mb-0"
                        />

                        <div className="form-group mb-20 flex-half">
                          <label>
                            Gender
                          </label>

                          <Select
                            name={`gender[${index}]`}
                            className="gender-select"
                            classNamePrefix="select"
                            options={genders}
                            placeholder="Gender"
                            onChange={(gender) => {
                              dispatch({
                                type: "legPaxFieldChanged",
                                payload: {
                                  legIndex: index,
                                  field: "gender",
                                  value: gender?.value || null,
                                  paxIndex: paxIndex,
                                },
                              });
                            }}
                            value={genders.find(({ value }) => value === passenger.gender) || null}
                            menuPosition="fixed"
                            isClearable
                          />
                        </div>

                        <div
                          className="form-group mb-10 mr-20 align-self-end itinerary-passenger-row-save-checkbox">
                          <Checkbox
                            checked={passenger.save}
                            onChange={(e) => {
                              dispatch({
                                type: "legPaxFieldChanged",
                                payload: {
                                  legIndex: index,
                                  field: "save",
                                  value: e.target.checked || false,
                                  paxIndex: paxIndex,
                                },
                              });
                            }}
                            name={`pax[save][${index}]`}
                          />

                          <label>Save?</label>
                        </div>
                      </>
                    )}

                    <div className="align-self-end itinerary-passenger-row-actions">
                      <button
                        className="deleteLeg base-button icon-button btn-danger"
                        onClick={(e) => {
                          e.preventDefault();
                          return dispatch({
                            type: "paxDeleted",
                            legIndex: index,
                            paxIndex: paxIndex,
                          });
                        }}
                        type="button"
                        title={`Delete Passenger ${index + 1}`}
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  </div>
                ))}

                <hr className="mt-10 mb-20"></hr>

                <h5 className="d-flex">
                  Crew

                  <button
                    className="base-button icon-button icon-button-small evo-dark-green ml-2"
                    title="Add Member"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({
                        type: "crewAdded",
                        legIndex: index,
                      });
                    }}
                  >
                    <span className="material-icons">add_circle</span>
                  </button>
                </h5>

                {leg?.crew?.map((member, memberIndex) => (
                  <div key={member._id || memberIndex} className="flex-row column-gap-1pct flex-align-center flex-wrap">
                    <div className="form-group  w-25">
                      <label>Position</label>

                      <Select
                        key={`crew-member-options`}
                        className="owner-select"
                        classNamePrefix="select"
                        options={[
                          {
                            label: "Captain",
                            value: "Captain",
                          },
                          {
                            label: "Second",
                            value: "Second",
                          },
                          {
                            label: "Cabin",
                            value: "Cabin",
                          },
                        ]}
                        value={member?.position}
                        placeholder="Member Position"
                        onChange={(selectedPosition) => {
                          dispatch({
                            type: "legCrewFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "position",
                              value: selectedPosition,
                              memberIndex: memberIndex,
                            },
                          });
                        }}
                      />
                    </div>
                    <TextInput
                      type={"text"}
                      name={`crew[firstName][${index}]`}
                      label="First Name"
                      handleChange={(e) => {
                        dispatch({
                          type: "legCrewFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "firstName",
                            value: e.target.value || "",
                            memberIndex: memberIndex,
                          },
                        });
                      }}
                      className="base-input mb-0"
                      containerClasses="flex-half mb-0"
                      value={member.firstName}
                    />
                    <TextInput
                      type={"text"}
                      name={`middleName[${index}]`}
                      label="Middle Name"
                      handleChange={(e) => {
                        dispatch({
                          type: "legCrewFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "middleName",
                            value: e.target.value || "",
                            memberIndex: memberIndex,
                          },
                        });
                      }}
                      className="base-input mb-0"
                      containerClasses="flex-half mb-0"
                      value={member.middleName}
                    />
                    <TextInput
                      type={"text"}
                      name={`lastName[${index}]`}
                      label="Last Name"
                      handleChange={(e) => {
                        dispatch({
                          type: "legCrewFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "lastName",
                            value: e.target.value || "",
                            memberIndex: memberIndex,
                          },
                        });
                      }}
                      className="base-input mb-0"
                      containerClasses="flex-half mb-0"
                      value={member.lastName}
                    />

                    <div
                      className="form-group flex-half ml-0"
                      key={`crew-phoneNumber-${index}`}
                    >
                      <label className="phoneInputLabel" htmlFor="phone">
                        Phone Number
                      </label>

                      <PhoneInput
                        placeholder="Phone Number"
                        value={member.phoneNumber}
                        name={`prospectPhone[${index}]`}
                        onChange={(value) => {
                          dispatch({
                            type: "legCrewFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "phoneNumber",
                              value: value || "",
                              memberIndex: memberIndex,
                            },
                          });
                        }}
                        onCountryChange={(value) => {
                          dispatch({
                            type: "legCrewFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "phoneCountry",
                              value: value || "",
                              memberIndex: memberIndex,
                            },
                          });
                        }}
                        defaultCountry={member.phoneCountry}
                        className="mb-0 base-phone-input"
                      />
                    </div>

                    <div className="align-self-end itinerary-passenger-row-actions">
                      {memberIndex >= 1 && (
                        <button
                          className="deleteLeg base-button icon-button btn-danger"
                          onClick={(e) => {
                            e.preventDefault();
                            return dispatch({
                              type: "crewDeleted",
                              legIndex: index,
                              memberIndex: memberIndex,
                            });
                          }}
                          type="button"
                          title={`Delete Member ${memberIndex + 1}`}
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <hr className="mt-10 mb-20"></hr>

                {/* start ground transport */}
                <h5 className="d-flex">
                  Ground Transportation

                  <button
                    className="base-button icon-button icon-button-small evo-dark-green ml-2"
                    title="Add Ground Transportation"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({
                        type: "legGroundTransportationAdded",
                        legIndex: index,
                      });
                    }}
                  >
                    <span className="material-icons">add_circle</span>
                  </button>
                </h5>

                {leg?.groundTransportation?.map((item, itemIndex) => (
                  <div key={item._id || itemIndex} className="flex-row column-gap-1pct flex-align-center flex-wrap">
                    <TextAreaInput
                      type={"text"}
                      name={`groundTransportation[${itemIndex}]`}
                      label="Add Ground Transportation Notes"
                      handleChange={(e) => {
                        dispatch({
                          type: "legGroundTransportationFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "note",
                            value: e.target.value || "",
                            itemIndex: itemIndex,
                          },
                        });
                      }}
                      className="base-input mb-0 textarea-auto-expand"
                      containerClasses="flex-half mb-0"
                      value={item.note}
                    />

                    <div className="align-self-start itinerary-passenger-row-actions mt-25 mb-0">
                      {itemIndex >= 1 && (
                        <button
                          className="deleteGroundTransportation base-button icon-button btn-danger"
                          onClick={(e) => {
                            e.preventDefault();
                            return dispatch({
                              type: "legGroundTransportationDeleted",
                              legIndex: index,
                              itemIndex: itemIndex,
                            });
                          }}
                          type="button"
                          title={`Delete Ground Transportation ${itemIndex + 1}`}
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {/* end ground transport */}

                <hr className="mt-10 mb-20"></hr>

                {/* start catering */}
                <h5 className="d-flex">
                  Leg Catering

                  <button
                    className="base-button icon-button icon-button-small evo-dark-green ml-2"
                    title="Add Catering Item"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({
                        type: "legCateringAdded",
                        legIndex: index,
                      });
                    }}
                  >
                    <span className="material-icons">add_circle</span>
                  </button>
                </h5>

                {leg?.catering?.map((item, itemIndex) => (
                  <div key={item._id || itemIndex} className="flex-row column-gap-1pct flex-align-start flex-wrap">
                    <div className="form-group flex-half mb-0">
                      <label>
                        Catering Item <span className="required">*</span>
                      </label>

                      <CreatableSelect
                        classNamePrefix="select"
                        placeholder="Enter Catering Item"
                        isClearable
                        options={state.cateringOptions}
                        filterOption={filterCateringOption}
                        onChange={(item) => {
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "item",
                              value: item || null,
                              itemIndex: itemIndex,
                            },
                          });
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "name",
                              value: item?.label || "",
                              itemIndex: itemIndex,
                            },
                          });
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "itemId",
                              value: item?.value || null,
                              itemIndex: itemIndex,
                            },
                          });
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "servings",
                              value: item?.servings || "",
                              itemIndex: itemIndex,
                            },
                          });
                        }}
                        onInputChange={(inputValue) => {
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "itemInputValue",
                              value: inputValue || "",
                              itemIndex: itemIndex,
                            },
                          });
                        }}
                        onCreateOption={(inputValue) => {
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "item",
                              value: {
                                label: inputValue,
                                value: null,
                              },
                              itemIndex: itemIndex,
                            },
                          });
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "name",
                              value: inputValue || "",
                              itemIndex: itemIndex,
                            },
                          });
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "itemId",
                              value: null,
                              itemIndex: itemIndex,
                            },
                          });
                          dispatch({
                            type: "legCateringFieldChanged",
                            payload: {
                              legIndex: index,
                              field: "servings",
                              value: "",
                              itemIndex: itemIndex,
                            },
                          });
                        }}
                        inputValue={item.itemInputValue || ""}
                        value={item.item}
                        name={`catering-item[${itemIndex}]`}
                      />
                    </div>

                    <TextInput
                      type={"number"}
                      name={`catering-quantity[${itemIndex}]`}
                      label="Quantity"
                      handleChange={(e) => {
                        dispatch({
                          type: "legCateringFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "quantity",
                            value: parseInt(e.target.value) || "",
                            itemIndex: itemIndex,
                          },
                        });
                      }}
                      className="base-input mb-0"
                      containerClasses="flex-quarter mb-0"
                      value={item.quantity}
                      min={1}
                      required
                    />

                    <TextInput
                      type={"text"}
                      name={`catering-servings[${itemIndex}]`}
                      label="Serving Size"
                      handleChange={(e) => {
                        dispatch({
                          type: "legCateringFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "servings",
                            value: e.target.value || "",
                            itemIndex: itemIndex,
                          },
                        });
                      }}
                      className="base-input mb-0"
                      containerClasses="flex-quarter mb-0"
                      value={item.servings}
                    />

                    <TextAreaInput
                      name={`catering-notes[${itemIndex}]`}
                      label="Item Notes"
                      handleChange={(e) => {
                        dispatch({
                          type: "legCateringFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "notes",
                            value: e.target.value || "",
                            itemIndex: itemIndex,
                          },
                        });
                      }}
                      className="base-input mb-0 textarea-auto-expand"
                      containerClasses="flex-one mb-0"
                      value={item.notes}
                      rows={1}
                    />

                    <button
                      className="deleteLegCateringItem base-button icon-button btn-danger mt-25"
                      onClick={(e) => {
                        e.preventDefault();
                        return dispatch({
                          type: "legCateringDeleted",
                          legIndex: index,
                          itemIndex: itemIndex,
                        });
                      }}
                      type="button"
                    >
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                ))}
                {/* end catering */}

                <hr className="mt-10 mb-20"></hr>

                {/* start leg notes */}
                <h5 className="d-flex">
                  Leg Notes

                  <button
                    className="base-button icon-button icon-button-small evo-dark-green ml-2"
                    title="Add Leg Notes"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({
                        type: "legNoteAdded",
                        legIndex: index,
                      });
                    }}
                  >
                    <span className="material-icons">add_circle</span>
                  </button>
                </h5>

                {leg?.notes?.map((item, itemIndex) => (
                  <div key={item._id || itemIndex} className="flex-row column-gap-1pct flex-align-center flex-wrap">
                    <TextAreaInput
                      type={"text"}
                      name={`legNote[${itemIndex}]`}
                      label="Add Leg Notes"
                      handleChange={(e) => {
                        dispatch({
                          type: "legNotesFieldChanged",
                          payload: {
                            legIndex: index,
                            field: "note",
                            value: e.target.value || "",
                            itemIndex: itemIndex,
                          },
                        });
                      }}
                      className="base-input textarea-auto-expand"
                      containerClasses="flex-half mb-0"
                      value={item.note}
                    ></TextAreaInput>

                    <div className="align-self-start itinerary-passenger-row-actions mt-25">
                      {itemIndex >= 1 && (
                        <button
                          className="deleteLegNote base-button icon-button btn-danger"
                          onClick={(e) => {
                            e.preventDefault();
                            return dispatch({
                              type: "legNoteDeleted",
                              legIndex: index,
                              itemIndex: itemIndex,
                            });
                          }}
                          type="button"
                          title={`Delete Leg Note ${itemIndex + 1}`}
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {/* end leg notes */}

                {index >= 1 && (
                  <button
                    className="deleteLeg action-button  delete-button mt-10"
                    onClick={(e) => {
                      e.preventDefault();
                      return dispatch({ type: "legDeleted", index: index });
                    }}
                    type="button"
                  >
                    Delete Leg {index + 1}
                  </button>
                )}
              </div>
            ))}
          <button
            className="action-button base-blue-button mt-0"
            onClick={handleAddLeg}
            type="button"
          >
            Add Leg
          </button>

          <h5 className="mt-30">Email Options</h5>

          <div className="w-100 mt-0 d-flex gap-20">
            <TextInput
              name="firstName"
              label="First Name"
              handleChange={handleEmailOptionsTextInputChange}
              value={state.itineraryData?.emailOptions?.firstName}
              required
              containerClasses="flex-half mb-0"
            />

            <TextInput
              name="lastName"
              label="Last Name"
              handleChange={handleEmailOptionsTextInputChange}
              value={state.itineraryData?.emailOptions?.lastName}
              required
              containerClasses="flex-half mb-0"
            />

            <div className="form-group flex-one">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <CreatableSelect
                className="email-select"
                classNamePrefix="select"
                placeholder="Email"
                components={emailComponents}
                isClearable
                isMulti
                menuIsOpen={false}
                onChange={handleEmailOptionsEmailChange}
                onInputChange={handleEmailInputChange}
                onKeyDown={handleEmailKeyDown}
                inputValue={state.emailInputValue}
                value={state.itineraryData?.emailOptions?.email}
                name="email"
                inputId="email"
              />
            </div>
          </div>

          <div className="w-100 mt-0 d-flex gap-20">
            <div className="flex-one">
              <div className="form-group">
                <label htmlFor="cc">CC</label>
                <CreatableSelect
                  className="email-select"
                  classNamePrefix="select"
                  placeholder="Cc"
                  components={emailComponents}
                  isClearable
                  isMulti
                  menuIsOpen={false}
                  onChange={handleEmailOptionsCCChange}
                  onInputChange={handleCcInputChange}
                  onKeyDown={handleCcKeyDown}
                  inputValue={state.ccInputValue}
                  value={state.itineraryData?.emailOptions?.cc}
                  inputId="cc"
                  name="cc"
                />
              </div>

              <input
                id="ccOwner"
                name="ccOwner"
                type="checkbox"
                checked={
                  state.itineraryData?.emailOptions?.ccOwner ? "checked" : ""
                }
                onChange={handleEmailOptionsCCOwnerChange}
              />
              <label htmlFor="ccOwner" className="ml-1">
                <b>CC OWNER</b>
              </label>
            </div>

            <div className="flex-one">
              <div className="form-group">
                <label htmlFor="bcc">BCC</label>

                <CreatableSelect
                  className="email-select"
                  classNamePrefix="select"
                  placeholder="Bcc"
                  components={emailComponents}
                  isClearable
                  isMulti
                  menuIsOpen={false}
                  onChange={handleEmailOptionsBCCChange}
                  onInputChange={handleBccInputChange}
                  onKeyDown={handleBccKeyDown}
                  inputValue={state.bccInputValue}
                  value={state.itineraryData?.emailOptions?.bcc}
                  inputId="bcc"
                  name="bcc"
                />
              </div>

              <input
                id="bccSender"
                name="bccSender"
                type="checkbox"
                checked={
                  state.itineraryData?.emailOptions?.bccSender ? "checked" : ""
                }
                onChange={handleEmailOptionsBCCSenderChange}
              />
              <label htmlFor="bccSender" className="ml-1">
                <b>BCC SENDER</b>
              </label>
            </div>
          </div>
        </div>

        <LoadingButton
          className="base-button evo-blue w-100"
          type="submit"
          isLoading={state.isFormLoading}
        >
          Save and Preview
        </LoadingButton>
      </form>

      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>

      <ReactModal
        isOpen={state.showOFRCopyModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseOFRCopyModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h5>Apply to all legs?</h5>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            Do you want to apply the same OFR to all other legs as well?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleConfirmOFRCopy}
            >
              Yes
            </button>

            <button
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleCloseOFRCopyModal}
            >
              No
            </button>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default NewItineraryContent;
